
import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import type { IconName } from "../Icon";
import { Tabs } from "./Tabs";

// Abschnitts-Navigation für lange Detailseiten (Standort, Ladepunkt).
// Eine Tab-Leiste auf allen Breiten; auf schmalen Viewports scrollt sie
// horizontal. Bewusst KEIN Kachelgrid auf Mobile: Kacheln standen dort direkt
// unter den Stat-Karten und waren von ihnen kaum zu unterscheiden.
// Der Consumer rendert immer nur den aktiven Abschnitt, damit Abschnitte erst
// beim Öffnen laden.

/** Ton einer Kachel. Farbe signalisiert **Zustand**, nicht Dekoration:
 *  neutral = normal, warning/danger = etwas will Aufmerksamkeit. */
export type SectionTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface SectionDef<T extends string = string> {
  id: T;
  label: string;
  iconName: IconName;
  tone?: SectionTone;
  /** Zahl neben dem Label ("n Einträge"). `undefined` = kein Zähler.
   *  Eine ausdrückliche `0` wird gezeigt: „kein Uplink aktiv“ ist die Zahl, auf
   *  die es ankommt — sie zu verschlucken hieße, den Alarmfall zu verstecken. */
  count?: number;
  /** Freitext statt Zähler — wird in der Tab-Leiste nicht angezeigt. */
  hint?: string;
}

// Statische Klassen je Ton — bewusst ausgeschrieben. Dynamisch
// zusammengesetzte Klassennamen sieht Tailwinds Scanner nicht und baut sie
// nicht mit (der Grund, warum die Badges auf der Baustellen-Seite leer sind).
const TONE_COUNT: Record<SectionTone, string> = {
  neutral: "text-content-3",
  info: "text-info-fg",
  success: "text-success-fg",
  warning: "text-warning-fg",
  danger: "text-danger-fg",
};

interface SectionNavProps<T extends string> {
  sections: readonly SectionDef<T>[];
  value: T;
  onChange: (id: T) => void;
  className?: string;
}

export function SectionNav<T extends string>({ sections, value, onChange, className = "" }: SectionNavProps<T>) {
  const barRef = useRef<HTMLDivElement>(null);

  // Aktiven Tab horizontal ins Bild holen — auf schmalen Viewports scrollt die
  // Leiste, und per ?tab=Deeplink kann der aktive Tab außerhalb liegen.
  useEffect(() => {
    const el = barRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    el?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, [value]);

  return (
    <div ref={barRef} className={className}>
      <Tabs
        value={value}
        onChange={onChange}
        tabs={sections.map((s) => ({
          value: s.id,
          iconName: s.iconName,
          label: (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              {s.label}
              {s.count !== undefined && (
                <span className={`text-xs ${TONE_COUNT[s.tone ?? "neutral"]}`}>{s.count}</span>
              )}
            </span>
          ),
        }))}
      />
    </div>
  );
}

/**
 * Aktiven Abschnitt in `?tab=` halten — deep-linkbar und Back-Button-fähig.
 *
 * Bewusst ohne Auto-Scroll: die Tab-Leiste ist beim Antippen ohnehin im Bild
 * und der Abschnitt beginnt direkt darunter; ein zusätzlicher Sprung wäre nur
 * unruhig. (Beim Kachelgrid davor war das nötig.)
 *
 * @param sections Registry — ein unbekannter `?tab=`-Wert fällt auf `fallback`.
 */
export function useSectionTab<T extends string>(sections: readonly SectionDef<T>[], fallback: T) {
  const [searchParams, setSearchParams] = useSearchParams();

  const param = searchParams.get("tab");
  const active = sections.some((s) => s.id === param) ? (param as T) : fallback;

  const setActive = useCallback(
    (id: T) => {
      const next = new URLSearchParams(searchParams.toString());
      next.set("tab", id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return { active, setActive };
}

export default SectionNav;
